import './PriceMatchBanner.scss';

import { CMSPriceMatchBanner } from '@travelwin/cms/types';
import { ButtonV2, Icon } from '@travelwin/core';
import classNames from 'classnames';
import { ReactNode } from 'react';

import { Link } from '@/components/Link';
import { serializeLexicalChildNode } from '@/components/RichText/serialize-lexical';
import { useExternalHomepageConfig } from '@/hooks/useExternalHomepageConfig';

const className = 'c-PriceMatchBanner';

interface PriceMatchContent {
  icon: CMSPriceMatchBanner['icon'];
  color: CMSPriceMatchBanner['bannerTheme'];
  title: string;
  description: ReactNode;
  cta?: {
    href: string;
    title: string;
    newTab?: boolean;
  };
}

export interface PriceMatchBannerType {
  priceMatchBanner: CMSPriceMatchBanner;
}

const PriceMatchBanner = ({ priceMatchBanner }: PriceMatchBannerType) => {
  const { getLocalisedUrl } = useExternalHomepageConfig();

  const content = {
    icon: priceMatchBanner.icon ?? 'awardCheck',
    color: priceMatchBanner.bannerTheme ?? 'light-yellow',
    title: priceMatchBanner.title,
    description: serializeLexicalChildNode({
      content: priceMatchBanner.description,
    }),
    cta:
      priceMatchBanner.cta?.href && priceMatchBanner.cta?.title
        ? {
            href: priceMatchBanner.cta.href,
            title: priceMatchBanner.cta.title,
            newTab: priceMatchBanner.cta.newTab ?? false,
          }
        : undefined,
  } satisfies PriceMatchContent;

  return (
    <div className={classNames(className, `${className}--${content.color}`)}>
      <div className={`${className}__icon`}>
        <Icon name={content.icon} />
      </div>
      <div className={`${className}__container`}>
        <div className={`${className}__title`}>{content.title}</div>
        <div className={`${className}__content`}>{content.description}</div>

        {content.cta && (
          <ButtonV2
            variant="link"
            size={'small'}
            className={`${className}__content-link-btn`}
            asChild
          >
            {content.cta.href.startsWith('/') ? (
              <Link
                to={content.cta.href}
                target={content.cta.newTab ? '_blank' : undefined}
              >
                {content.cta.title}
              </Link>
            ) : (
              <a
                href={getLocalisedUrl(content.cta.href)}
                target={content.cta.newTab ? '_blank' : undefined}
              >
                {content.cta.title}
              </a>
            )}
          </ButtonV2>
        )}
      </div>
    </div>
  );
};

export default PriceMatchBanner;
